import React, { useEffect, useState } from "react";
import {
  Button,
  ConfigProvider,
  Modal,
  notification,
  Space,
  Table,
  Upload,
} from "antd";
import {
  deleteFileInFileManagement,
  downloadFile as downloadFileApi,
  getFileListInFileManagement,
  uploadFileInFileManagement,
} from "../../../common/request";
import ResponseCode from "../../../common/response-code";
import {
  downloadFile as generateFileAndDownload,
  prettyBytes,
} from "../../../plugins/utils";
import { useSelector } from "react-redux";
import { CloudUploadOutlined } from "@ant-design/icons";
import Styles from "./ConfigFileManagement.module.scss";
import { errorModal } from "../../../plugins/utils";

/** 机构管理-机构配置-文件管理 */
function ConfigFileManagement({ orgItem, modalVisible }) {
  const [tableData, setTableData] = useState({});
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const envInfo = useSelector((state) => state.env.envInfo);

  useEffect(() => {
    if (orgItem.id && modalVisible) getTableData();
  }, [orgItem, modalVisible, pageNum, pageSize]);

  /** 获取列表数据 */
  const getTableData = async () => {
    setLoading(true);
    try {
      const params = { orgId: orgItem.id, pageNum, pageSize };
      const res = await getFileListInFileManagement(params);
      setLoading(false);
      if (res.code !== ResponseCode.success) return errorModal(res.msg);
      setTableData(res.data);
    } catch (error) {
      setLoading(false);
      console.error(error);
      errorModal("网络错误，查询文件列表失败");
    }
  };

  /** 分页器变化 */
  const paginationChange = (num, size) => {
    setPageSize(size);
    setPageNum(size === pageSize ? num : 1);
  };

  /** 上传文件 */
  const fileUpload = (info) => {
    const { status } = info.file;
    if (status === "done") {
      getTableData();
    } else if (status === "error") {
      errorModal("上传失败");
    }
  };

  /** 删除文件确认 */
  const deleteFileConfirm = (item) => {
    const { fileName, id } = item;
    Modal.confirm({
      title: "删除文件",
      content: `确定要删除文件【${fileName}】吗？`,
      onOk: () => deleteFile(id),
    });
  };

  /** 删除文件 */
  const deleteFile = async (id) => {
    setLoading(true);
    try {
      const res = await deleteFileInFileManagement({ id });
      setLoading(false);
      if (res.code !== ResponseCode.success) return errorModal(res.msg);
      notification.success({ message: "操作成功" });
      getTableData();
    } catch (error) {
      setLoading(false);
      console.error(error);
      errorModal("网络错误，删除文件失败");
    }
  };

  /** 下载文件 */
  const downloadFileAction = async (row) => {
    try {
      setLoading(true);
      const { fileName, filePath } = row;
      const res = await downloadFileApi({ fileUrl: filePath });
      const lastDotIndex = fileName.lastIndexOf(".");
      const fileType =
        lastDotIndex > -1 ? fileName.substring(lastDotIndex + 1) : "";
      const fileNameWithoutExt =
        lastDotIndex > -1 ? fileName.substring(0, lastDotIndex) : fileName;
      generateFileAndDownload(res, fileType, fileNameWithoutExt);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
      errorModal("网络错误，下载文件失败");
    }
  };

  /** 上传控件 props */
  const uploaderProps = {
    name: "file",
    multiple: true,
    action: uploadFileInFileManagement,
    headers: { authorization: window.sessionStorage.getItem("access_token") },
    data: { orgId: orgItem.id },
    showUploadList: false,
    accept: ".rar,.zip,.doc,.docx,.pdf,.jpg",
    onChange: fileUpload,
  };

  /** 表格操作栏按钮 */
  const operationBtnList = [
    { label: "下载", fn: downloadFileAction },
    { label: "删除", fn: deleteFileConfirm },
  ];

  /** 表格项 */
  const columns = [
    { title: "文件名", dataIndex: "fileName" },
    { title: "文件类型", dataIndex: "fileType" },
    {
      title: "文件大小",
      dataIndex: "fileSize",
      render: (value) => prettyBytes(value, 2),
    },
    { title: "生成时间", dataIndex: "createTime" },
    {
      title: "操作",
      dataIndex: "operation",
      render: (_, row) => {
        return (
          <Space className={"d-flex justify-content-end"}>
            {operationBtnList.map((item) => (
              <Button
                type={"primary"}
                ghost
                danger={item.label === "删除"}
                onClick={() => item.fn(row)}
                key={item.label}
              >
                {item.label}
              </Button>
            ))}
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <ConfigProvider componentSize={"small"}>
        <Upload.Dragger {...uploaderProps}>
          <CloudUploadOutlined className={Styles.icon} />
          <div className={Styles.tip}>将文件拖到此处，或点击上传</div>
          <div className={Styles.typeTip}>
            支持扩展名：.rar .zip .doc .docx .pdf .jpg
          </div>
        </Upload.Dragger>

        <Table
          columns={columns}
          dataSource={tableData.elements}
          rowKey={(record) => record.id}
          loading={loading}
          pagination={{
            showTotal: (total) => `共 ${total} 条`,
            size: "normal",
            showSizeChanger: true,
            total: tableData.totalElements,
            pageSize,
            current: pageNum,
            onChange: paginationChange,
          }}
        />
      </ConfigProvider>
    </>
  );
}

export default ConfigFileManagement;
